'use client';

import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { containsXss } from '@/utils/validate-input';

export interface SurveyModalProps {
  surveyId: string;
  title?: string;
  onClose?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function sanitizeSurveyId(input: string): string {
  if (typeof input !== 'string') return '';
  return input.replace(/[^a-zA-Z0-9_]/g, '').trim();
}

export function SurveyModal({
  surveyId,
  onClose,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: SurveyModalProps) {
  const sanitizedSurveyId = sanitizeSurveyId(surveyId);

  if (!sanitizedSurveyId || containsXss(surveyId)) {
    return null;
  }

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined && controlledOnOpenChange !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const surveyContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isControlled) setInternalOpen(true);
  }, [isControlled]);

  useEffect(() => {
    if (!isOpen) return;

    const observer = new MutationObserver(() => {
      const widget = document.querySelector('.smcx-widget');
      if (widget && surveyContainerRef.current && !surveyContainerRef.current.contains(widget)) {
        surveyContainerRef.current.appendChild(widget);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (isControlled) controlledOnOpenChange(open);
    else setInternalOpen(open);
    if (!open && onClose) onClose();
  };

  return (
    <>
      {isOpen && (
        <Script
          id="academy-survey"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `(function(t,e,s,n){var o,a,c;t.SMCX=t.SMCX||[],e.getElementById(n)||(o=e.getElementsByTagName(s),a=o[o.length-1],c=e.createElement(s),c.type="text/javascript",c.async=!0,c.id=n,c.src="https://widget.surveymonkey.com/collect/website/js/${sanitizedSurveyId}.js",a.parentNode.insertBefore(c,a))})(window,document,"script","smcx-sdk");`,
          }}
        />
      )}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="survey-modal max-w-[600px] rounded-lg border-0 bg-surface-3 p-0 shadow-lg"
          showCloseButton={true}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="survey-modal-body min-h-[320px] w-full p-0">
            <div id="survey-container" ref={surveyContainerRef} className="survey-widget-container" />
          </div>
          <DialogFooter className="flex justify-end border-t border-border-medium py-3 px-4">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="cs-s-body text-amethyst-accent hover:underline"
            >
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SurveyModal;
